import { useApiClient, ApiError } from '../lib/api-client.js';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

/**
 * Owns the form-state spine every entity FormPage hand-rolls: default state,
 * edit-mode load, validation, save mutation, dirty tracking. Pairs with
 * `EntityFormShell` but is usable standalone.
 */
function useEntityForm({ resource, itemId, defaults, queryKey, fromApi, toApi, validate, onSuccess, onNotFound, onError, }) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const isEdit = !!itemId;
    const [formData, setFormData] = useState(defaults);
    const [errors, setErrors] = useState({});
    const [lastSaved, setLastSaved] = useState(null);
    const [isFormReady, setIsFormReady] = useState(!isEdit);
    const seededForIdRef = useRef(null);
    const snapshotRef = useRef(defaults);
    const applyBaseline = useCallback((next) => {
        snapshotRef.current = next;
        setFormData(next);
    }, []);
    const { data: loadedResponse, isLoading, isError, } = useQuery({
        queryKey: [queryKey, itemId],
        queryFn: () => apiClient.get(`/${resource}/${itemId}`),
        enabled: isEdit,
    });
    useEffect(() => {
        if (!isEdit)
            return;
        const entity = loadedResponse?.data;
        if (!entity)
            return;
        if (seededForIdRef.current === itemId)
            return;
        const next = fromApi(entity);
        applyBaseline(next);
        if (entity.updatedAt)
            setLastSaved(new Date(entity.updatedAt));
        setIsFormReady(true);
        seededForIdRef.current = itemId ?? null;
    }, [isEdit, itemId, loadedResponse, fromApi, applyBaseline]);
    useEffect(() => {
        if (isEdit && isError && onNotFound)
            onNotFound();
    }, [isEdit, isError, onNotFound]);
    const updateField = useCallback((key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => {
            if (!prev[key])
                return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);
    const isDirty = useMemo(() => {
        const baseline = snapshotRef.current;
        const keys = new Set([...Object.keys(baseline), ...Object.keys(formData)]);
        for (const key of keys) {
            if (baseline[key] !== formData[key])
                return true;
        }
        return false;
    }, [formData]);
    const saveMutation = useMutation({
        mutationFn: async (payload) => {
            if (isEdit) {
                return apiClient.put(`/${resource}/${itemId}`, payload);
            }
            return apiClient.post(`/${resource}`, payload);
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: [queryKey] });
            const entity = response.data;
            setLastSaved(new Date());
            applyBaseline(fromApi(entity));
            onSuccess?.(entity, { isEdit });
        },
        onError: (err) => {
            let handled = false;
            const helpers = {
                setFieldErrors: (next) => {
                    setErrors(next);
                    handled = true;
                    const firstKey = Object.keys(next)[0];
                    if (firstKey && typeof document !== 'undefined') {
                        document
                            .getElementById(`field-${firstKey}`)
                            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                },
                markHandled: () => {
                    handled = true;
                },
            };
            onError?.(err, helpers);
            if (handled)
                return;
            // Auto-map ApiError.details → field errors when the caller didn't handle it.
            if (err instanceof ApiError && err.details?.length) {
                const fieldErrors = {};
                for (const detail of err.details) {
                    const fieldName = detail.path?.[0];
                    if (fieldName && !fieldErrors[fieldName]) {
                        fieldErrors[fieldName] = detail.message;
                    }
                }
                if (Object.keys(fieldErrors).length > 0) {
                    helpers.setFieldErrors(fieldErrors);
                }
            }
        },
    });
    const submit = useCallback(() => {
        const nextErrors = validate(formData);
        const hasErrors = Object.keys(nextErrors).length > 0;
        setErrors(nextErrors);
        if (hasErrors) {
            const firstKey = Object.keys(nextErrors)[0];
            const el = typeof document !== 'undefined'
                ? document.getElementById(`field-${firstKey}`)
                : null;
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        saveMutation.mutate(toApi(formData));
    }, [formData, saveMutation, toApi, validate]);
    return {
        formData,
        errors,
        setErrors,
        updateField,
        submit,
        isLoading,
        isSubmitting: saveMutation.isPending,
        isFormReady,
        lastSaved,
        isDirty,
        isNotFound: isEdit && isError,
        isEdit,
    };
}

export { useEntityForm };
//# sourceMappingURL=useEntityForm.js.map
